/**
 * HOLD: make the product gallery's dots work.
 *
 * Dawn draws dots for its slideshow block and drives them from
 * SlideshowComponent - active class, aria-current, click to jump. The
 * product gallery is a plain <slider-component>, which only updates the
 * numeric counter it normally renders. Swapping the fraction for dots in
 * snippets/product-media-gallery.liquid therefore gives the look without
 * the behaviour, and this supplies the rest.
 *
 * Deliberately not done by making the gallery a SlideshowComponent: that
 * class also owns autoplay, announcement bars and a focus model built for
 * a hero banner, and media-gallery.js already coordinates this slider with
 * the thumbnails and the modal. Borrowing the whole class to get two lines
 * of state would be the larger change, not the smaller one.
 *
 * Position is read from the slider's own scrollLeft rather than tracked
 * here, so a swipe, an arrow, a thumbnail and a jump all end in the same
 * place with one source of truth.
 */
(() => {
  const init = (wrap) => {
    const slider = wrap.closest('slider-component')?.querySelector('.slider');
    if (!slider) return;
    const dots = Array.from(wrap.querySelectorAll('.slider-counter__link'));
    const current = wrap.querySelector('.slider-counter--current');
    if (dots.length < 2) {
      // One image needs no pagination, and an empty row of one dot reads as
      // a broken control rather than as nothing.
      wrap.hidden = true;
      return;
    }

    const slides = () => Array.from(slider.querySelectorAll('.product__media-item'));

    const indexNow = () => {
      const items = slides();
      if (!items.length) return 0;
      // Nearest slide to the left edge, tolerant of fractional scroll
      // positions and of a container that peeks the next image.
      let best = 0;
      let bestGap = Infinity;
      items.forEach((item, i) => {
        const gap = Math.abs(item.offsetLeft - slider.scrollLeft);
        if (gap < bestGap) {
          bestGap = gap;
          best = i;
        }
      });
      return best;
    };

    const paint = () => {
      const i = indexNow();
      dots.forEach((dot, n) => {
        const on = n === i;
        dot.classList.toggle('slider-counter__link--active', on);
        if (on) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
      if (current) current.textContent = String(i + 1);
    };

    let queued = false;
    slider.addEventListener(
      'scroll',
      () => {
        if (queued) return;
        queued = true;
        window.requestAnimationFrame(() => {
          queued = false;
          paint();
        });
      },
      { passive: true }
    );

    dots.forEach((dot, n) => {
      dot.addEventListener('click', () => {
        const item = slides()[n];
        if (item) slider.scrollTo({ left: item.offsetLeft, behavior: 'smooth' });
      });
    });

    paint();
  };

  const start = () => document.querySelectorAll('[data-hold-gallery-dots]').forEach(init);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
